const SOURCE_OF_TRUTH_MISSING_MESSAGE =
  "This team doesn't exist in the source of truth anymore and might have been deleted.";

const hasEntries = (value) =>
  value != null && typeof value === "object" && Object.keys(value).length > 0;

/**
 * Parent id from the source hierarchy. previousDirectParentId is the parent at
 * the last TeamForm move and is not a source location.
 */
const getSourceParentId = (directParent) =>
  directParent?.upstreamDirectParentId ?? null;

/**
 * Type from the source hierarchy. previousType is the type at the last
 * TeamForm move and is not a source type.
 */
const getSourceType = (groupTypeOverride) =>
  groupTypeOverride?.upstreamType ?? null;

const hasParentOverride = (directParent) =>
  !!(
    directParent?.directParentId ||
    directParent?.previousDirectParentId ||
    directParent?.upstreamDirectParentId
  );

const hasGroupTypeOverride = (groupTypeOverride) =>
  !!(groupTypeOverride?.upstreamType || groupTypeOverride?.previousType);

/**
 * Advanced move that stored a type map and did not change the parent.
 */
const isTypeOnlyAdvancedMove = (propertyOverrides) => {
  const directParent = propertyOverrides?.directParent;
  if (!hasEntries(directParent?.groupTypeMap)) {
    return false;
  }
  return !hasParentOverride(directParent);
};

const sourceTypeDiffers = (group) => {
  const sourceType = getSourceType(group?.propertyOverrides?.groupType);
  return !!sourceType && group?.type !== sourceType;
};

/**
 * A type override is stored, but import has no upstream type for it.
 */
const isSourceTypeMissing = (group) =>
  hasGroupTypeOverride(group?.propertyOverrides?.groupType) &&
  !getSourceType(group?.propertyOverrides?.groupType);

/**
 * @returns {"parent" | "type" | null}
 */
const getPersistedMoveKind = (group) => {
  const propertyOverrides = group?.propertyOverrides;
  if (!propertyOverrides) {
    return null;
  }

  if (hasParentOverride(propertyOverrides.directParent)) {
    return "parent";
  }

  if (
    sourceTypeDiffers(group) ||
    isTypeOnlyAdvancedMove(propertyOverrides) ||
    isSourceTypeMissing(group)
  ) {
    return "type";
  }

  return null;
};

export {
  SOURCE_OF_TRUTH_MISSING_MESSAGE,
  getPersistedMoveKind,
  getSourceParentId,
  getSourceType,
  isSourceTypeMissing,
  isTypeOnlyAdvancedMove,
};
